import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export const DEFAULT_EDUCATOR_POSITION_OPTIONS = [
  'Teacher',
  'Admin',
  'Registrar',
  'Para',
  'ESE Support',
  'Office staff',
  'lunch staff',
  'guidance counselor',
] as const;

export interface WebsiteNcofEducatorFormConfig {
  id: string;
  slug: string;
  is_enabled: boolean;
  eyebrow: string;
  hero_title: string;
  hero_intro: string;
  info_title: string;
  info_bullets: string[];
  info_footer: string;
  form_title: string;
  form_intro: string;
  tutoring_question: string;
  success_message: string;
  position_options: string[];
  updated_at?: string;
}

export type WebsiteNcofEducatorFormConfigUpdate = Omit<
  WebsiteNcofEducatorFormConfig,
  'id' | 'slug' | 'updated_at'
>;

function asStringArray(value: unknown, fallback: string[]): string[] {
  if (!Array.isArray(value)) return fallback;
  return value
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter(Boolean);
}

function normalizeConfig(row: Record<string, unknown>): WebsiteNcofEducatorFormConfig {
  return {
    id: String(row.id ?? ''),
    slug: String(row.slug ?? 'default'),
    is_enabled: Boolean(row.is_enabled ?? true),
    eyebrow: String(row.eyebrow ?? 'School Partnerships'),
    hero_title: String(row.hero_title ?? 'NCOF Educators'),
    hero_intro: String(row.hero_intro ?? ''),
    info_title: String(row.info_title ?? 'Who this is for'),
    info_bullets: asStringArray(row.info_bullets, []),
    info_footer: String(row.info_footer ?? ''),
    form_title: String(row.form_title ?? 'Educator signup'),
    form_intro: String(row.form_intro ?? ''),
    tutoring_question: String(row.tutoring_question ?? 'Would you be interested in tutoring?'),
    success_message: String(
      row.success_message ??
        'Thank you! Your NCOF Educators signup has been submitted. Our team will follow up soon.'
    ),
    position_options: asStringArray(
      row.position_options,
      [...DEFAULT_EDUCATOR_POSITION_OPTIONS]
    ),
    updated_at: row.updated_at ? String(row.updated_at) : undefined,
  };
}

export const useWebsiteNcofEducatorFormConfig = (enabled = true) => {
  return useQuery<WebsiteNcofEducatorFormConfig | null>({
    queryKey: ['websiteNcofEducatorFormConfig'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('website_ncof_educator_form_config')
        .select(
          'id, slug, is_enabled, eyebrow, hero_title, hero_intro, info_title, info_bullets, info_footer, form_title, form_intro, tutoring_question, success_message, position_options, updated_at'
        )
        .eq('slug', 'default')
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return normalizeConfig(data as Record<string, unknown>);
    },
    enabled,
    staleTime: 30_000,
  });
};

export const useSaveWebsiteNcofEducatorFormConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: WebsiteNcofEducatorFormConfigUpdate) => {
      const { data, error } = await supabase
        .from('website_ncof_educator_form_config')
        .upsert(
          {
            slug: 'default',
            is_enabled: payload.is_enabled,
            eyebrow: payload.eyebrow.trim(),
            hero_title: payload.hero_title.trim(),
            hero_intro: payload.hero_intro.trim(),
            info_title: payload.info_title.trim(),
            info_bullets: payload.info_bullets.map((item) => item.trim()).filter(Boolean),
            info_footer: payload.info_footer.trim(),
            form_title: payload.form_title.trim(),
            form_intro: payload.form_intro.trim(),
            tutoring_question: payload.tutoring_question.trim(),
            success_message: payload.success_message.trim(),
            position_options: payload.position_options.map((item) => item.trim()).filter(Boolean),
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'slug' }
        )
        .select(
          'id, slug, is_enabled, eyebrow, hero_title, hero_intro, info_title, info_bullets, info_footer, form_title, form_intro, tutoring_question, success_message, position_options, updated_at'
        )
        .single();

      if (error) throw error;
      return normalizeConfig(data as Record<string, unknown>);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['websiteNcofEducatorFormConfig'] });
    },
  });
};
